import { createReactBlockSpec } from '@blocknote/react';
import * as React from 'react';
import { create } from 'zustand';
import { formatISO, isValid, parseISO } from 'date-fns';

import { EditableDateField } from '@heartplus/ui';

const normalizeLabel = (
  label?: string | { default_text?: string; defaultText?: string },
) => {
  if (typeof label === 'string') {
    return label;
  }
  if (label && typeof label === 'object') {
    return label.default_text || label.defaultText || '';
  }
  return '';
};

const formatDateValue = (value?: string) => {
  if (!value) {
    return undefined;
  }
  try {
    const date = parseISO(value);
    if (isValid(date)) {
      return date;
    }
  } catch {
    // ignore
  }
  return undefined;
};

const toIsoDate = (date?: Date) => {
  if (!date) {
    return '';
  }
  return formatISO(date, { representation: 'date' });
};

interface DateBlockState {
  currentValue: string;
  setCurrentValue: (value: string) => void;
}

const createDateBlockStore = (initialValue: string) =>
  create<DateBlockState>((set) => ({
    currentValue: initialValue,
    setCurrentValue: (nextValue: string) => set({ currentValue: nextValue }),
  }));

const createDateBlockSpec = createReactBlockSpec(
  {
    type: 'date',
    propSchema: {
      value: {
        default: '',
      },
      label: {
        default: 'Select a date',
      },
      tokenId: {
        default: '',
      },
    },
    content: 'none',
  },
  {
    // BlockNote's render function is a React component
    // Type is inferred from createReactBlockSpec - no need for explicit any
    render: (props) => {
      const block = props.block;
      if (!block || !props.editor) {
        return null;
      }

      const dateBlockStore = React.useMemo(
        () => createDateBlockStore(block.props?.value ?? ''),
        // Empty deps: store is created once per block instance, updated via useEffect
        [],
      );
      const currentValue = dateBlockStore((state) => state.currentValue);
      const setCurrentValue = dateBlockStore((state) => state.setCurrentValue);

      React.useEffect(() => {
        const newValue = block.props?.value ?? '';
        if (newValue !== currentValue) {
          setCurrentValue(newValue);
        }
      }, [block.props?.value, currentValue, setCurrentValue]);

      const handleDateChange = (date?: Date | undefined) => {
        const nextValue = toIsoDate(date);
        setCurrentValue(nextValue);
        if (!props.editor || !block) {
          return;
        }

        props.editor.updateBlock(block, {
          type: 'date',
          props: {
            ...block.props,
            value: nextValue,
          },
        });
      };

      const parsedDate = formatDateValue(currentValue);
      const label = normalizeLabel(block.props?.label) || 'Select a date';

      return (
        <div
          // Ensures BlockNote doesn't treat this as editable text and swallow pointer events.
          contentEditable={false}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className="inline-flex"
        >
          <EditableDateField
            value={parsedDate}
            onChange={handleDateChange}
            placeholder={label}
            defaultEditable={false}
            openOnEdit={true}
            fitContent
            // Smaller + inline for phrases like "from <date> to <date>"
            className="h-6 px-1 py-0 text-xs leading-5"
            dateFormat="MM/dd/yyyy"
          />
        </div>
      );
    },
    // Parse HTML to convert back to block (optional, for copy/paste support)
    parse: (element: HTMLElement) => {
      const input = element.querySelector('input[type="date"]');
      if (input) {
        return {
          value: (input as HTMLInputElement).value || '',
          label: input.getAttribute('data-label') || 'Select a date',
          tokenId: input.getAttribute('data-token-id') || '',
        };
      }
      return undefined;
    },
  },
);

// Export the concrete spec object (call the factory once)
export const dateBlockSpec = createDateBlockSpec();

// Backwards-compatible alias (kept for existing imports)
export const createDateBlock = dateBlockSpec;
